(() => {
  const cache = new Map();
  const ambiguousCodes = new Set(['S14']);
  const registerCodeOwners = rows => {
    const owners = new Map();
    rows.forEach(row => row[4].forEach(code => {
      if (!owners.has(code)) owners.set(code, new Set());
      owners.get(code).add(row[0]);
    }));
    owners.forEach((makes, code) => { if (makes.size > 1) ambiguousCodes.add(code); });
  };
  const unique = values => [...new Set(values)].sort((a, b) => a.localeCompare(b, undefined, { numeric: true }));
  const queryFor = selection => {
    const quote = value => `"${value.replace(/["\\]/g, '')}"`;
    const names = selection.model.replace(/\([^)]*\)/g, '').split(/\s*\/\s*/).map(name => name.trim()).filter(Boolean);
    const model = names[0];
    const aliases = new Set(names);
    names.forEach(name => {
      if (/[A-Za-z]-\d|[A-Za-z]-[A-Za-z]/.test(name)) aliases.add(name.replace(/-/g, ''));
      if (/^WRX STI$/i.test(name)) aliases.add('STI');
      if (selection.make === 'Ford' && /^F\d{2,3}$/.test(name)) aliases.add(name.replace(/^(F)(\d+)/, '$1-$2'));
      if (name === 'Grand Prix') aliases.add('Gran Prix');
    });
    const chassis = selection.chassis === 'all' ? '' : selection.chassis;
    if (model === 'Model not specified') return chassis ? `${quote(selection.make)} ${quote(chassis)}` : quote(selection.make);
    if (chassis && chassis.length >= 3 && /\d/.test(chassis) && !/^(?:MK|V|GEN|SERIES)\s*\d+$/i.test(chassis) && !ambiguousCodes.has(chassis)) return chassis;
    if (chassis) {
      const modelTerms = /^\d+$/.test(model) || model.length <= 2 ? [quote(`${selection.make} ${model}`), quote(`${selection.make}${model}`)] : [...aliases].map(quote);
      const context = [...modelTerms, quote(`${selection.make} ${chassis}`), quote(`${chassis} ${selection.make}`)];
      if (chassis.length >= 3 && ambiguousCodes.has(chassis) && !/^(?:MK|V|GEN|SERIES)\s*\d+$/i.test(chassis)) context.push(quote(selection.make));
      return `(${context.join(' OR ')}) ${quote(chassis)}`;
    }
    if (/^\d+$/.test(model) || model.length <= 2) {
      const aliases = [quote(`${selection.make} ${model}`), quote(`${selection.make}${model}`)];
      if (selection.make === 'Toyota' && model === '86') aliases.push('GT86', quote('GT-86'), 'FT86');
      return aliases.join(' OR ');
    }
    return [...aliases].map(quote).join(' OR ');
  };
  const yearsFor = rows => {
    const years = new Set();
    for (let year = 1950; year <= new Date().getFullYear() + 1; year++) years.add(year);
    rows.forEach(row => {
      if (row[2] !== null) for (let year = row[2]; year <= row[3]; year++) years.add(year);
    });
    return [...years].sort((a, b) => b - a).map(String);
  };
  const chassisFor = (rows, year) => unique(rows.flatMap(row => row[4])).filter(code => {
    if (year === 'all') return true;
    const dated = rows.filter(row => row[4].includes(code) && row[2] !== null);
    const specific = dated.filter(row => row[4].length === 1);
    const ranges = specific.length ? specific : dated;
    return !ranges.length || ranges.some(row => Number(year) >= row[2] && Number(year) <= row[3]);
  });
  const fetchOptions = async url => {
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 18000);
    try {
      const response = await fetch(url, { credentials: 'same-origin', signal: controller.signal });
      if (!response.ok) throw new Error('Request failed');
      const data = await response.json();
      if (!Array.isArray(data.vehicles)) throw new Error('Invalid vehicle options');
      return data.vehicles;
    } finally { clearTimeout(timeout); }
  };
  const init = root => {
    if (root.dataset.vfReady) return;
    root.dataset.vfReady = 'true';
    const t = JSON.parse(root.querySelector('[data-vf-translations]').textContent);
    const form = root.querySelector('form');
    const make = form.elements.vf_make;
    const model = form.elements.vf_model;
    const year = form.elements.vf_year;
    const chassis = form.elements.vf_chassis;
    const query = form.elements.q;
    const submit = form.querySelector('[type="submit"]');
    const status = root.querySelector('.jdm-vf__status');
    let rows, loading;
    const options = (select, values, placeholder) => {
      select.replaceChildren(new Option(placeholder, ''));
      values.forEach(value => select.add(new Option(value.label ?? value, value.value ?? value)));
      select.disabled = values.length === 0;
    };
    const selected = () => ({ make: make.value, model: model.value, year: year.value, chassis: chassis.value });
    const updateSubmit = () => {
      submit.disabled = !make.value || !model.value || !year.value || !chassis.value;
      query.value = submit.disabled ? '' : queryFor(selected());
    };
    const vehicleRows = () => rows.filter(row => row[0] === make.value && row[1] === model.value);
    const updateChassis = () => {
      const values = chassisFor(vehicleRows(), year.value);
      options(chassis, [{ value: 'all', label: t.allChassis }, ...values], t.chooseChassis);
      if (values.length === 1) chassis.value = values[0];
      else if (!values.length) chassis.value = 'all';
      updateSubmit();
    };
    const updateYears = () => {
      options(year, [{ value: 'all', label: t.allYears }, ...yearsFor(vehicleRows())], t.chooseYear);
      year.value = 'all';
      updateChassis();
    };
    const updateModels = () => {
      options(model, unique(rows.filter(row => row[0] === make.value).map(row => row[1])), t.chooseModel);
      options(year, [], t.chooseYear);
      options(chassis, [], t.chooseChassis);
      updateSubmit();
    };
    const search = () => {
      updateSubmit();
      if (submit.disabled) return;
      const selection = selected();
      try { sessionStorage.setItem('jdmVehicle', JSON.stringify(selection)); } catch {}
      const url = new URL(form.action, location.origin);
      url.searchParams.set('q', queryFor(selection));
      url.searchParams.set('type', 'product');
      url.searchParams.set('options[prefix]', 'last');
      url.searchParams.set('options[unavailable_products]', 'last');
      Object.entries(selection).forEach(([key, value]) => url.searchParams.set(`vf_${key}`, value));
      const preview = new URL(location.href).searchParams.get('preview_theme_id');
      if (preview) url.searchParams.set('preview_theme_id', preview);
      location.assign(url.href);
    };
    const restore = () => {
      const url = new URL(location.href);
      let saved;
      if (url.searchParams.has('vf_make')) saved = Object.fromEntries(['make', 'model', 'year', 'chassis'].map(key => [key, url.searchParams.get(`vf_${key}`) || '']));
      else if (location.pathname.indexOf('/search') === -1) {
        try { saved = JSON.parse(sessionStorage.getItem('jdmVehicle') || 'null'); } catch {}
      }
      if (!saved) return;
      make.value = saved.make;
      if (!make.value) return;
      updateModels();
      model.value = saved.model;
      if (!model.value) return;
      updateYears();
      if (saved.year === 'unknown') saved.year = 'all';
      if ([...year.options].some(option => option.value === saved.year)) year.value = saved.year;
      updateChassis();
      if (saved.chassis === 'unknown') saved.chassis = 'all';
      if ([...chassis.options].some(option => option.value === saved.chassis)) chassis.value = saved.chassis;
      updateSubmit();
      if (root.dataset.legacyResults === 'true' && !submit.disabled) search();
    };
    const load = async () => {
      if (rows) return rows;
      if (loading) return loading;
      status.textContent = t.loading;
      make.disabled = true;
      loading = (async () => {
        try {
          if (!cache.has(root.dataset.options)) cache.set(root.dataset.options, fetchOptions(root.dataset.options));
          rows = await cache.get(root.dataset.options);
          registerCodeOwners(rows);
          options(make, unique(rows.map(row => row[0])), t.chooseMake);
          status.textContent = '';
          restore();
          return rows;
        } catch {
          cache.delete(root.dataset.options);
          loading = null;
          make.disabled = false;
          status.textContent = t.loadError;
          return null;
        }
      })();
      return loading;
    };
    make.addEventListener('focus', load);
    form.addEventListener('pointerdown', () => { if (!rows) void load(); }, { passive: true });
    make.addEventListener('change', async () => { if (!rows) await load(); if (rows) updateModels(); });
    model.addEventListener('change', updateYears);
    year.addEventListener('change', updateChassis);
    chassis.addEventListener('change', updateSubmit);
    form.addEventListener('submit', async event => {
      event.preventDefault();
      if (!rows) await load();
      if (rows && form.reportValidity()) search();
    });
    root.querySelector('.jdm-vf__reset').addEventListener('click', () => {
      make.value = '';
      if (rows) updateModels();
      status.textContent = '';
      try { sessionStorage.removeItem('jdmVehicle'); } catch {}
      const url = new URL(location.href);
      ['make', 'model', 'year', 'chassis', 'engine', 'application', 'incomplete'].forEach(key => url.searchParams.delete(`vf_${key}`));
      history.replaceState(null, '', url.href);
    });
    if (root.dataset.legacyResults === 'true' || new URL(location.href).searchParams.has('vf_make')) void load();
    else if ('IntersectionObserver' in window) {
      const observer = new IntersectionObserver(entries => {
        if (entries.some(entry => entry.isIntersecting)) { observer.disconnect(); void load(); }
      }, { rootMargin: '150px' });
      observer.observe(root);
    } else void load();
  };
  const start = () => document.querySelectorAll('[data-jdm-vehicle-finder]').forEach(init);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start, { once: true }); else start();
  document.addEventListener('shopify:section:load', start);
})();
